import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTreatmentPlan } from "../../../../actions/treatmentPlanActions";
import {
  backgroundLight,
  buttonRed,
  buttonRedText,
  primaryAlt,
  secondary,
} from "../../../../utils/colors";
import {
  Button,
  Flex,
  Box,
  Heading,
  Text,
  useToast,
  IconButton,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  white,
  newBorder,
  textPrimary,
  textSecondary,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import mongoose from "mongoose";
import dayjs from "dayjs";
import lt from "dayjs/locale/lt";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import { FaArrowLeftLong } from "react-icons/fa6";
import { TbDiscount, TbSwitch } from "react-icons/tb";
import AddServiceModal from "../../../../components/modals/AddServiceModal";
import AddPatientModal from "../../../../components/modals/AddPatientModal";
import AddCoordinatorModal from "../../../../components/modals/AddCoordinatorModal";
import UserSelectButton from "../../../../components/buttons/UserSelectButton.js";
import ServiceListNew from "../../../../components/partials/ServiceListNew.js";
// import StatusButtonNew from "../../../../components/buttons/StatusButtonNew.js";
import StatusButtonTreatmentPlan from "../../../../components/buttons/StatusButtonTreatmentPlan.js";
// import WarningModal from "../../../../components/buttons/partials/WarningModal.js";
import AddServiceButton from "../partials/AddServiceButton.js";
import FormControlTextArea from "../../../../components/input/FormControlTextArea.js";
import { useParams } from "react-router-dom";
import useIsMobile from "../../../../hooks/useIsMobile";
import { FaXmark } from "react-icons/fa6";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol.js";
import ServiceListNewMobile from "../../../../components/partials/ServiceListNewMobile.js";
import { MdLocalHospital } from "react-icons/md";

// Extend Day.js with the plugins
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

// Define custom relative time strings for English (if needed)
dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few sec",
    m: "1 min",
    mm: "%d min",
    h: "1 h",
    hh: "%d h",
    d: "1 d",
    dd: "%d d",
    M: "1 m",
    MM: "%d m",
    y: "1 y",
    yy: "%d y",
  },
});

// Define custom relative time strings for Lithuanian
dayjs.updateLocale("lt", {
  relativeTime: {
    future: "po %s",
    past: "prieš %s",
    s: "kelios sek.",
    m: "1 min.",
    mm: "%d min.",
    h: "1 val.",
    hh: "%d val.",
    d: "1 d.",
    dd: "%d d.",
    M: "1 mėn.",
    MM: "%d mėn.",
    y: "1 m.",
    yy: "%d m.",
  },
});

dayjs.locale("lt");

const CreateTreatmentPlanNew = ({
  tenant,
  currentPatient,
  close,
  isPatientPage,
  currency,
}) => {
  // GENERAL
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  // STATE
  const [services, setServices] = useState([]);
  const [totalTpPrice, setTotalTpPrice] = useState(0);
  const [coordinator, setCoordinator] = useState(null);
  const [patient, setPatient] = useState(currentPatient);
  const [status, setStatus] = useState("draft");
  const [notes, setNotes] = useState("");

  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
  const [addCoordinatorModalOpen, setAddCoordinatorModalOpen] = useState(false);

  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  const [isServicesValid, setIsServicesValid] = useState(false);
  const isServicesSelected = services.length > 0;
  const isPatientSelected = patient !== null;
  const isCoordinatorSelected = coordinator !== null;

  // services state

  const [oneLeftToComplete, setOneLeftToComplete] = useState(false);
  const [oneLeftToUncomplete, setOneLeftToUncomplete] = useState(false);

  // Toggle Advanced Mode and save to local storage
  const toggleAdvancedMode = () => {
    const newMode = !isAdvancedMode;
    setIsAdvancedMode(newMode);
    localStorage.setItem("isAdvancedMode", newMode);
  };

  // REDUX

  const checkStatusOnAddService = (services) => {
    if (services.length === 0) {
      setStatus("draft");
      return;
    } else {
      setStatus("planning");
    }
  };

  const checkStatusOnRemoveService = (services) => {
    if (services.length === 0) {
      setStatus("draft");
      return;
    } else {
      setStatus("planning");
    }
  };

  // Function to check statuses and update the state
  // Updated checkServiceStatuses function to set the new states
  const checkServiceStatuses = (services) => {
    console.log("CHECKING SERVICE STATUSES");
    const completedCount = services.filter(
      (service) => service.status === "completed"
    ).length;
    const uncompletedCount = services.filter(
      (service) => service.status === "uncompleted"
    ).length;

    // Determine if there is exactly one service left to be completed
    const oneLeftToComplete =
      completedCount === services.length - 1 && uncompletedCount === 1;
    // Determine if there is exactly one service left to be uncompleted
    const oneLeftToUncomplete =
      uncompletedCount === services.length - 1 && completedCount === 1;

    // Update state variables

    setOneLeftToComplete(oneLeftToComplete);
    setOneLeftToUncomplete(oneLeftToUncomplete);
  };

  // };

  // CHECK HANDLERS

  // SUBMIT HANDLER

  const submitHandler = (e) => {
    e.preventDefault();

    // updateTreatmentPlanStatus(services); // NEW commented

    // Ensure patient object structure is correct
    const patientId = isPatientPage ? patient : patient?.value || patient;

    // console.log("submit data: ", {
    //   patient: patientId,
    //   employee: userInfo && userInfo._id,
    //   services: services,
    //   status: status,
    //   coordinator: coordinator.value,
    //   notes
    // });

    // console.log("userInfo", userInfo);

    // console.log("dispatching treatment plan", {
    //   patient: patientId,
    //   employee: userInfo._id,
    //   services: services,
    //   status: status,
    //   coordinator: coordinator.value,
    //   notes,
    // });

    // console.log("coordinator.value", coordinator.value);

    dispatch(
      createTreatmentPlan({
        slug,
        patient: patientId,
        // employee: userInfo.employeeUserId,
        services: services,
        status: status,
        coordinator: coordinator.value,
        notes,
      })
    );

    // close();
  };

  // HANDLERS
  // const handleAddService = () => {
  //   setAddServiceModalOpen(!addServiceModalOpen);
  // };

  const handleAddPatient = () => {
    setAddPatientModalOpen(!addPatientModalOpen);
  };

  const handleAddCoordinator = () => {
    setAddCoordinatorModalOpen(!addCoordinatorModalOpen);
  };

  const closeHandler = () => {
    close();
  };

  const addServiceToList = (selectedService) => {
    // console.log("selectedService", selectedService);

    // Clone the selectedService to a new object
    const newService = {
      ...selectedService, // Spread to copy properties
      _id: new mongoose.Types.ObjectId(), // ✅ Correct way
      // _id: Math.random().toString(36).substr(2, 9), // ✅ Random unique ID
      appointment: null, // Set the appointment to null by default
      employee: null, // Set the employee to null by default
      order: services.length + 1, // Set the order based on the current list length
      paid: false, // You can set other default values here
      price:
        typeof selectedService.price === "object"
          ? { ...selectedService.price }
          : selectedService.price,
      priceVariations: selectedService.priceVariations,
      totalPrice:
        selectedService.priceVariations === true ? 0 : selectedService.price,
      service: {
        ...selectedService.service, // Deep copy if 'service' is an object
        _id: selectedService.value,
        name: selectedService.label,
        serviceGroup: selectedService.serviceGroup,
        employee: null,
        price: selectedService.price,
      },
      status: "uncompleted", // Set the initial status as needed
      teeth: [], // Set the initial teeth array as needed
      quantity: selectedService.priceVariations === false ? 1 : 0,
      discountValue: 0,
      discountType: "none",
    };

    // console.log("newService", newService);

    // Add the new service to the list of services
    setServices((prevServices) => [...prevServices, newService]);

    setTotalTpPrice(totalTpPrice + newService.totalPrice);

    // updateTreatmentPlanStatus([...services, newService]);
    checkStatusOnAddService([...services, newService]);

    if (!isAdvancedMode) {
      setAddServiceModalOpen(false);
    } else {
      toast({
        title: t("toasts.serviceAdded"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // const saveEditedService = (updatedService) => {
  //   // console.log("Updated Service:", updatedService);

  //   const newServices = services.map((service) =>
  //     service._id === updatedService._id ? updatedService : service
  //   );

  //   // Reorder the services based on 'order' attribute
  //   newServices.sort((a, b) => a.order - b.order);

  //   // Adjust order for all services
  //   newServices.forEach((service, index) => {
  //     service.order = index + 1;
  //   });

  //   // checkAllServicesStatus(newServices);

  //   // checkStatusOnSaveEditedService(newServices);

  //   setTotalTpPrice(
  //     newServices.reduce((total, service) => total + service.totalPrice, 0)
  //   );
  //   setServices(newServices);

  //   // Update the status states based on the new services
  //   checkServiceStatuses(newServices);
  // };
  const saveEditedService = (updatedService) => {
    const newServices = services.map((service) =>
      service._id === updatedService._id ? updatedService : service
    );

    newServices.sort((a, b) => a.order - b.order);
    newServices.forEach((service, index) => {
      service.order = index + 1;
    });

    const updatedTotal = newServices.reduce((total, service) => {
      const quantity = Number(service.quantity) || 0;
      let price = service.price;

      if (service.discountType === "percentage") {
        price = price - (price * service.discountValue) / 100;
      } else if (service.discountType === "amount") {
        price = price - service.discountValue;
      }

      const lineTotal = Math.max(price, 0) * quantity; // prevent negative
      return total + lineTotal;
    }, 0);

    setServices(newServices);
    setTotalTpPrice(updatedTotal);
    checkServiceStatuses(newServices);
  };

  const removeService = (serviceId) => {
    const updatedServices = services
      .filter((service) => service._id !== serviceId)
      .map((service, index) => ({ ...service, order: index + 1 }));

    setServices(updatedServices);
    setTotalTpPrice(
      updatedServices.reduce((total, service) => total + service.totalPrice, 0)
    );

    checkStatusOnRemoveService(updatedServices);
    // updateTreatmentPlanStatus(updatedServices);
  };

  // VALIDATE SERVICES
  const validateServices = (services) => {
    // Check if any service has a quantity of 0
    const isAnyServiceInvalid = services.some(
      (service) => service.quantity === 0
    );

    // If any service is invalid, set form validity to false
    setIsServicesValid(!isAnyServiceInvalid);
  };

  // RESET SERVICES STATUS TO UNCOMPLETED
  const resetServicesStatusToUncompleted = () => {
    // console.log("RESSETING SERVICES STATUS TO UNCOMPLETED");
    setServices((prevServices) => {
      const updatedServices = prevServices.map((service) => ({
        ...service,
        status: "uncompleted",
      }));

      return updatedServices;
    });
  };

  // USE EFFECT
  useEffect(() => {
    // Check if the advanced mode is set in local storage
    const isAdvancedMode = localStorage.getItem("isAdvancedMode") === "true";
    setIsAdvancedMode(isAdvancedMode);
  }, []);

  // Initial call to set the state correctly when component loads
  useEffect(() => {
    checkServiceStatuses(services);
  }, [services]);

  useEffect(() => {
    // console.log("loaded");
    validateServices(services);
  }, [services]);

  const handleServiceOrderChange = (newOrder) => {
    // Update the services with the new order
    setServices(newOrder);
  };

  // useEffect(() => {
  //   if (userInfo) {
  //     setCoordinator({
  //       value: userInfo.employeeUserId,
  //       label: `${userInfo.firstName} ${userInfo.lastName}`,
  //       firstName: userInfo.firstName,
  //       lastName: userInfo.lastName,
  //     });
  //   }
  // }, [userInfo]);

  useEffect(() => {
    if (tenant && tenant.defaultTreatmentPlanEmployee) {
      setCoordinator({
        value: tenant.defaultTreatmentPlanEmployee._id,
        label: `${tenant.defaultTreatmentPlanEmployee?.firstName || "-"} ${
          tenant.defaultTreatmentPlanEmployee?.lastName || ""
        }`,
        firstName: tenant.defaultTreatmentPlanEmployee?.firstName || "-",
        lastName: tenant.defaultTreatmentPlanEmployee?.lastName || "",
      });
    }
  }, [tenant]);

  const isAnyServiceDiscounted = useMemo(() => {
    return services.some(
      (service) => service.discountValue > 0 || service.discountType !== "none"
    );
  }, [services]);

  const discountedAmount = useMemo(() => {
    return services.reduce((total, service) => {
      if (service.discountType === "percentage") {
        return (
          total +
          ((service.price * service.discountValue) / 100) *
            (service.quantity || 1)
        );
      } else if (service.discountType === "amount") {
        return total + service.discountValue * (service.quantity || 1);
      }
      return total;
    }, 0);
  }, [services]);

  const reimbursedAmount = useMemo(() => {
    return services.reduce((total, service) => {
      if (
        service.reimbursement?.coverage > 0 &&
        service.reimbursement?.coverageType === "percent"
      ) {
        return (
          total +
          ((service.price * service.reimbursement.coverage) / 100) *
            (service.quantity || 1)
        );
      } else if (
        service.reimbursement?.coverage > 0 &&
        service.reimbursement?.coverageType === "fixed"
      ) {
        return total + service.reimbursement.coverage * (service.quantity || 1);
      }
      return total;
    }, 0);
  }, [services]);

  const isAnyServiceReimbursed = useMemo(() => {
    return services.some((service) => service.reimbursement?.coverage > 0);
  }, [services]);

  const currencySymbol = useCurrencySymbol(currency);

  // LOGS

  // console.log("tenant", tenant);

  return (
    <>
      {addServiceModalOpen && (
        <AddServiceModal
          addServiceModalOpen={addServiceModalOpen}
          setAddServiceModalOpen={setAddServiceModalOpen}
          addServiceToList={addServiceToList}
          isAdvancedMode={isAdvancedMode}
          toggleAdvancedMode={toggleAdvancedMode}
          currency={currency}
        />
      )}

      {addPatientModalOpen && (
        <AddPatientModal
          addPatientModalOpen={addPatientModalOpen}
          setAddPatientModalOpen={setAddPatientModalOpen}
          setPatient={setPatient}
        />
      )}

      {addCoordinatorModalOpen && (
        <AddCoordinatorModal
          addCoordinatorModalOpen={addCoordinatorModalOpen}
          setAddCoordinatorModalOpen={setAddCoordinatorModalOpen}
          setCoordinator={setCoordinator}
        />
      )}

      <Flex w="100%" h="100%" justify="flex-end" position="relative">
        <Box
          position="relative"
          bg={"white"}
          w="100%"
          maxW="100%"
          borderRadius="2rem"
          h="100%"
        >
          <form
            onSubmit={submitHandler}
            style={{
              height: "100%",
            }}
          >
            <Flex w="100%" h="100%" direction="column">
              <Flex
                justify="space-between"
                align="center"
                p={["1.25rem 1.5rem 1.25rem", "1.25rem 2rem 1.25rem"]}
                borderBottom={`1px solid ${newBorder}`}
                mb="0rem"
                position="relative"
                zIndex={90}
                // bg={greenText}
                w="100%"
              >
                <Flex align="center">
                  {!isMobile && (
                    <IconButton
                      bg={backgroundLight}
                      border={`1px solid ${backgroundLight}`}
                      color={textSecondary}
                      icon={<FaArrowLeftLong />}
                      borderRadius="10.75rem"
                      fontSize="0.875rem"
                      onClick={closeHandler}
                      mr="1rem"
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: primaryAlt,
                      }}
                    />
                  )}

                  <Flex align="center">
                    <Heading
                      fontSize={["1rem", "1.125rem"]}
                      color={textPrimary}
                      fontWeight="600"
                      mr={4}
                    >
                      {t("patient.createPatientTreatmentPlan")}
                    </Heading>
                    {/* {status} */}
                  </Flex>
                </Flex>

                <Flex
                  align="center"
                  justify="flex-end"
                  gap={["0.5rem", "1rem"]}
                >
                  <StatusButtonTreatmentPlan
                    isUnclickable={status === "draft"}
                    isSmall
                    currentStatus={status}
                    options="treatment"
                    onUpdateStatus={setStatus}
                    isServicesValid={isServicesValid}
                    resetServicesStatusToUncompleted={
                      resetServicesStatusToUncompleted
                    }
                    patient={patient}
                  />

                  {isMobile && (
                    <IconButton
                      position="relative"
                      // top="1.5rem"
                      // right="1.5rem"
                      size="sm"
                      bg={backgroundLight}
                      color={textSecondary}
                      onClick={closeHandler}
                      borderRadius="10rem"
                      _hover={{ bg: primaryAlt, color: white }}
                    >
                      <Icon as={FaXmark} />
                    </IconButton>
                  )}
                </Flex>
              </Flex>

              <Flex w="100%" h="100%" direction="column" overflowY="auto">
                <Flex
                  w="100%"
                  align="center"
                  justify="space-between"
                  p={["1rem 1.5rem", "1rem 2rem"]}
                  gap="1rem"
                  direction={["column", "row"]}
                >
                  <Flex w={["100%", "50%"]} align="center">
                    <Flex gap="0" direction="column" w="100%">
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        color={textPrimary}
                        m="0 0 0.5rem"
                      >
                        {t("patient.patient")}
                      </Text>
                      <Flex
                        w="100%"
                        direction="column"
                        border={`1px solid ${newBorder}`}
                        p="0.75rem 1rem 1rem"
                        borderRadius="1rem"
                        // gap="0.3rem"
                      >
                        <Flex align="center">
                          <Text
                            fontSize="0.8rem"
                            color={textSecondary}
                            fontWeight="500"
                            mr="0.25rem"
                          >
                            {t("patient.patient")}:{" "}
                          </Text>

                          <UserSelectButton
                            user={patient}
                            onClick={handleAddPatient}
                            isOpen={addPatientModalOpen}
                            isSecondary={false}
                            noUserLabel={t("treatmentPlan.selectPatient")}
                            isLocked={
                              isPatientPage ||
                              status === "approved" ||
                              status === "completed" ||
                              status === "cancelled" ||
                              status === "ongoing"
                            }
                            tpStatus={status}
                          />
                        </Flex>

                        <Flex align="center" m="0.25rem 0 0.4rem">
                          <Text
                            fontSize="0.8rem"
                            color={textSecondary}
                            fontWeight="500"
                          >
                            {t("user.columnEmail")}:
                            <Text
                              as="span"
                              fontSize="0.8rem"
                              color={textPrimary}
                              fontWeight="500"
                              ml="0.25rem"
                            >
                              {patient && patient.email ? patient.email : "-"}
                            </Text>
                          </Text>
                        </Flex>

                        <Text
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                        >
                          {t("service.phoneNumber")}:
                          <Text
                            as="span"
                            fontSize="0.8rem"
                            color={textPrimary}
                            fontWeight="500"
                            ml="0.25rem"
                          >
                            {patient && patient.phone ? patient.phone : "-"}
                          </Text>
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>

                  <Flex w={["100%", "50%"]} align="center">
                    <Flex gap="0" direction="column" w="100%">
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        color={textPrimary}
                        m="0 0 0.5rem"
                      >
                        {t("treatmentPlan.treatmentPlan")}
                      </Text>
                      <Flex
                        w="100%"
                        direction="column"
                        border={`1px solid ${newBorder}`}
                        p="0.75rem 1rem 1rem"
                        borderRadius="1rem"
                        // gap="0.3rem"
                      >
                        <Flex align="center">
                          <Text
                            fontSize="0.8rem"
                            color={textSecondary}
                            fontWeight="500"
                            mr="0.25rem"
                          >
                            {t("treatmentPlan.coordinatorShort")}:
                          </Text>

                          <UserSelectButton
                            user={coordinator}
                            onClick={handleAddCoordinator}
                            isOpen={addCoordinatorModalOpen}
                            isSecondary
                            noUserLabel={t("treatmentPlan.selectCoordinator")}
                            tpStatus={status}
                          />
                          {/* 
                               <UserSelectButton
                            user={patient}
                            onClick={handleAddPatient}
                            isOpen={addPatientModalOpen}
                            isSecondary={false}
                            noUserLabel={t("treatmentPlan.selectPatient")}
                            isLocked={
                              isPatientPage ||
                              status === "approved" ||
                              status === "completed" ||
                              status === "cancelled" ||
                              status === "ongoing"
                            }
                            tpStatus={status}
                          /> */}
                        </Flex>

                        <Flex align="center" m="0.25rem 0 0.4rem">
                          <Text
                            fontSize="0.8rem"
                            color={textSecondary}
                            fontWeight="500"
                          >
                            {t("form.creationDate")}:
                            <Text
                              as="span"
                              fontSize="0.8rem"
                              color={textPrimary}
                              textTransform="capitalize"
                              fontWeight="500"
                              ml="0.25rem"
                            >
                              {dayjs().format("YYYY MMMM D, HH:mm")}
                            </Text>
                          </Text>
                        </Flex>

                        <Text
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                        >
                          {t("treatmentPlan.lastUpdatedShort")}:
                          <Text
                            as="span"
                            fontSize="0.8rem"
                            color={textPrimary}
                            textTransform="capitalize"
                            fontWeight="500"
                            ml="0.25rem"
                          >
                            {dayjs().format("YYYY MMMM D, HH:mm")}
                          </Text>
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>

                <Flex
                  w="100%"
                  p={["0.5rem 0rem 2rem", "0.5rem 2rem 6.5rem"]}
                  direction="column"
                  // h="100%"
                >
                  <Flex
                    w="100%"
                    align="center"
                    justify="space-between"
                    m="0 0 0.5rem"
                    p={["0 1.5rem", "initial"]}
                  >
                    <Flex align="center">
                      <Flex align="center" mr="0.5rem">
                        <Text
                          fontSize="0.875rem"
                          fontWeight="600"
                          color={textPrimary}
                          mr="0.25rem"
                        >
                          {t("service.services")}:
                        </Text>

                        {services.length > 0 && (
                          <Text
                            fontWeight="500"
                            color={textPrimary}
                            fontSize="0.875rem"
                          >
                            {currencySymbol} {totalTpPrice}
                          </Text>
                        )}
                      </Flex>

                      {isAnyServiceDiscounted && (
                        <Flex align="center" ml="0.5rem">
                          <Flex
                            align="center"
                            justify="center"
                            textAlign="center"
                            mr={0}
                          >
                            <Icon
                              as={TbDiscount}
                              color={primaryAlt}
                              fontSize="0.8rem"
                              mr="0.125rem"
                              position="relative"
                              top="-1px"
                            />
                          </Flex>
                          <Text
                            fontSize="0.8rem"
                            fontWeight="500"
                            color={textSecondary}
                            mr="0.5rem"
                          >
                            {t("service.discountedAmount")}: {currencySymbol}{" "}
                            {discountedAmount}
                          </Text>
                        </Flex>
                      )}

                      {isAnyServiceReimbursed && (
                        <Flex align="center" ml="0.5rem">
                          <Flex
                            align="center"
                            justify="center"
                            textAlign="center"
                            mr={0}
                          >
                            <Icon
                              as={MdLocalHospital}
                              color={primaryAlt}
                              fontSize="0.8rem"
                              mr="0.125rem"
                              position="relative"
                              top="-1px"
                            />
                          </Flex>
                          <Text
                            fontSize="0.8rem"
                            fontWeight="500"
                            color={textSecondary}
                            mr="0.5rem"
                          >
                            {t("service.reimbursedAmount")}: {currencySymbol}{" "}
                            {reimbursedAmount}
                          </Text>
                        </Flex>
                      )}
                    </Flex>

                    <Flex align="center" justify="flex-end" gap="0.5rem">
                      <IconButton
                        size="sm"
                        fontSize="sm"
                        // borderRadius="1rem"
                        bg={backgroundLight}
                        border={`0px solid ${newBorder}`}
                        color={textSecondary}
                        borderRadius="10rem"
                        // onClick={() => setIsSimpleView(!isSimpleView)}
                        isDisabled
                        _hover={{ bg: primaryAlt, color: white }}
                      >
                        <Icon as={TbSwitch} fontSize="0.875rem" />
                      </IconButton>{" "}
                      <AddServiceButton
                        tpStatus={status}
                        setAddServiceModalOpen={setAddServiceModalOpen}
                        resetServicesStatusToUncompleted={
                          resetServicesStatusToUncompleted
                        }
                        setStatus={setStatus}
                      />
                    </Flex>
                  </Flex>

                  <Flex
                    w="100%"
                    bg={backgroundLight}
                    direction="column"
                    p={["1rem 0rem 0.25rem", "1rem 1rem 0.25rem"]}
                    borderRadius={["0rem", "0.875rem"]}
                    gap="0.75rem"
                    // minH="169px"
                  >
                    {!isMobile ? (
                      <ServiceListNew
                        services={services}
                        updateServiceOrder={handleServiceOrderChange}
                        saveEditedService={saveEditedService}
                        removeService={removeService}
                        isEdit={false}
                        tpStatus={status}
                        setStatus={setStatus}
                        oneLeftToComplete={oneLeftToComplete}
                        oneLeftToUncomplete={oneLeftToUncomplete}
                        resetServicesStatusToUncompleted={
                          resetServicesStatusToUncompleted
                        }
                        currency={currency}
                      />
                    ) : (
                      <ServiceListNewMobile
                        services={services}
                        updateServiceOrder={handleServiceOrderChange}
                        saveEditedService={saveEditedService}
                        removeService={removeService}
                        isEdit={false}
                        tpStatus={status}
                        setStatus={setStatus}
                        oneLeftToComplete={oneLeftToComplete}
                        oneLeftToUncomplete={oneLeftToUncomplete}
                        resetServicesStatusToUncompleted={
                          resetServicesStatusToUncompleted
                        }
                        currency={currency}
                      />
                    )}
                  </Flex>

                  {services.length > 0 && (
                    <Flex
                      w="100%"
                      m="2rem 0 0"
                      direction="column"
                      p={["0 1.5rem", "initial"]}
                    >
                      <Text
                        fontSize={"0.875rem"}
                        fontWeight="600"
                        color={textPrimary}
                        mb="1rem"
                      >
                        {t("treatmentPlan.tpNotesPatient")}
                      </Text>

                      <FormControlTextArea
                        label={t("form.notes")}
                        value={notes}
                        placeholder={t("placeholder.tpNotesPatient")}
                        onChange={(e) => setNotes(e.target.value)}
                        fontSize="0.8rem"
                        // isTouched={isTouched}
                        // isError={isError}
                        // errorMessage="This field is required"
                        w="100%"
                        h="initial"
                        minH={["100px", "36px"]}
                        // isRequired
                      />
                    </Flex>
                  )}
                </Flex>

                <Flex
                  align="center"
                  justify="flex-end"
                  position={["relative", "fixed"]}
                  bottom={["initial", "2rem"]}
                  right={["initial", "2rem"]}
                  gap="1rem"
                  p={["0 1.5rem 6rem", "initial"]}
                  zIndex={9000}
                >
                  <Tooltip
                    bg={buttonRed}
                    color={buttonRedText}
                    padding="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      <Flex direction="column" align="center">
                        {!isPatientSelected && (
                          <Text fontSize="xs" fontWeight="500">
                            {t("treatmentPlan.errorPatient")}
                          </Text>
                        )}
                        {!isCoordinatorSelected && (
                          <Text fontSize="xs" fontWeight="500">
                            {t("treatmentPlan.errorCoordinator")}
                          </Text>
                        )}
                        {!isServicesSelected && (
                          <Text fontSize="xs" fontWeight="500">
                            {t("treatmentPlan.errorServices")}
                          </Text>
                        )}
                        {!isServicesValid && (
                          <Text fontSize="xs" fontWeight="500">
                            {t("treatmentPlan.errorServicesQuantity")}
                          </Text>
                        )}
                      </Flex>
                    }
                    isDisabled={
                      isServicesSelected &&
                      isServicesValid &&
                      isPatientSelected &&
                      isCoordinatorSelected
                    }
                  >
                    <Button
                      minWidth="10rem"
                      bg={textPrimary}
                      size="lg"
                      h={["3.5rem", "3rem"]}
                      w={["100%", "initial"]}
                      color={white}
                      fontSize="md"
                      borderRadius="10rem"
                      border={`1px solid ${white}`}
                      _hover={{
                        bg: secondary,
                        color: white,
                        border: `1px solid ${white}`,
                      }}
                      isDisabled={
                        !isServicesSelected ||
                        !isServicesValid ||
                        !isPatientSelected ||
                        !isCoordinatorSelected
                      }
                      onClick={submitHandler}
                    >
                      {t("treatmentPlan.createTreatmentPlan")}
                    </Button>
                  </Tooltip>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </Box>
      </Flex>
    </>
  );
};

export default CreateTreatmentPlanNew;
